import { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";

import { AppRoutes } from "@/routing/Routes";
import { Toast } from "@/components/shared/Toast";
import { FlowProvider } from "@/contexts/FlowProvider";
import { AuthProvider } from "@/contexts/AuthProvider";
import { preloadFFmpeg } from "./utils/videoEditing";
import { DeployContextProvider } from "./contexts/DeployProvider";
import { TutorialProvider } from "@/contexts/TutorialProvider";
import { AnalyticsProvider } from "@/contexts/AnalyticsProvider";
import { FpsProvider } from "@/contexts/FpsProvider";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalProvider";
import { ProjectSelectorProvider } from "@/contexts/ProjectSelectorProvider";
import { UrlModalProvider } from "@/contexts/UrlModalProvider";
import { useCIAlerts } from "@/hooks/useCIAlerts";
import { useAuth } from "@/contexts/AuthContext";
import { useCheckoutCompletion } from "@/hooks/useCheckoutCompletion";
import LoginModal from "./components/auth/LoginModal";
import { ExperimentProvider } from "@scripts/react/hooks/ExperimentContext";
import { useCurrentWorkspace } from "@/store";

export function RapidExperimentProvider({ children }: { children: React.ReactNode }) {
    const { user, guestUid } = useAuth();
    const currentWorkspace = useCurrentWorkspace();
    const groups = useMemo(
        () => (currentWorkspace?.id ? { workspace: currentWorkspace.id } : undefined),
        [currentWorkspace?.id]
    );

    return (
        <ExperimentProvider distinctId={user?.uid ?? guestUid ?? ""} groups={groups}>
            {children}
        </ExperimentProvider>
    );
}

export function App() {
    // Pre-load FFmpeg as soon as the app starts
    useEffect(() => {
        preloadFFmpeg() // FFmpeg preload completed - trimming will be instant
            .catch((error) => console.warn("🎬 [FFmpeg] Preload failed:", error));
    }, []);

    return (
        <BrowserRouter>
            <ProjectSelectorProvider>
                <AuthProvider>
                    <RapidExperimentProvider>
                        <AnalyticsProvider>
                            <UpgradeModalProvider>
                                <FpsProvider>
                                    <FlowProvider>
                                        <DeployContextProvider>
                                            <TutorialProvider>
                                                <UrlModalProvider>
                                                    <AppContent />
                                                </UrlModalProvider>
                                            </TutorialProvider>
                                        </DeployContextProvider>
                                    </FlowProvider>
                                </FpsProvider>
                            </UpgradeModalProvider>
                        </AnalyticsProvider>
                    </RapidExperimentProvider>
                </AuthProvider>
            </ProjectSelectorProvider>
        </BrowserRouter>
    );
}

const AppContent = () => {
    const { isLoginModalOpen, closeLoginModal, guestAuthInitialized, initializeGuestAuth } =
        useAuth();

    // Initialize guest auth for all unauthenticated users
    // This enables API calls (like inference) to work without login
    useEffect(() => {
        if (!guestAuthInitialized) {
            initializeGuestAuth();
        }
    }, [guestAuthInitialized, initializeGuestAuth]);

    // Handle Stripe checkout completion redirect
    useCheckoutCompletion();

    // Listen for CI training evaluations and show toast alerts
    useCIAlerts();

    return (
        <>
            <AppRoutes />
            <Toast />
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
        </>
    );
};
